import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import Reveal from "@/components/site/Reveal";
import { submitLeadMagnet } from "@/lib/api";

export default function LeadMagnet() {
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!prenom.trim() || !email.trim()) {
      toast.error("Merci de remplir votre prénom et votre email.");
      return;
    }
    setLoading(true);
    try {
      await submitLeadMagnet({ prenom: prenom.trim(), email: email.trim() });
      setDone(true);
      toast.success("Merci ! Vous allez recevoir le guide par email.");
      setPrenom("");
      setEmail("");
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="guide"
      data-testid="lead-magnet-section"
      className="py-24 md:py-32 bg-[#FAF7F8]"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-[#E6D6DF] via-[#F3EEF4] to-[#FAF7F8] border border-[#DDD7E2] p-10 md:p-16 shadow-[0_30px_80px_-30px_rgba(58,46,110,0.2)]">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#C98FA3]/30 blur-[80px] pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#2847B8]/15 blur-[80px] pointer-events-none" />

            <div className="relative grid md:grid-cols-12 gap-10 items-center">
              <div className="md:col-span-7">
                <span className="text-sm uppercase tracking-[0.2em] text-[#3A2E6E] font-medium">
                  — Guide gratuit
                </span>
                <h2 className="font-serif-display text-4xl md:text-5xl font-light mt-4 leading-[1.05] text-[#1E1E2A]">
                  Comprendre ce qui se répète
                </h2>
                <p className="mt-6 text-base md:text-lg text-[#1E1E2A]/80 leading-relaxed max-w-lg">
                  Recevez un guide simple pour identifier les schémas émotionnels qui
                  influencent votre vie.
                </p>
                <p className="mt-3 text-sm text-[#5F6170] max-w-lg">
                  Vous recevrez également les prochaines actualités et dates d'ateliers.
                </p>
              </div>

              <div className="md:col-span-5">
                <form
                  onSubmit={submit}
                  data-testid="lead-magnet-form"
                  className="bg-white rounded-2xl p-6 md:p-7 border border-[#DDD7E2] space-y-4 shadow-[0_10px_30px_rgba(58,46,110,0.06)]"
                >
                  <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-[#5F6170] font-medium">
                      Prénom
                    </label>
                    <Input
                      data-testid="lead-magnet-prenom"
                      type="text"
                      value={prenom}
                      onChange={(e) => setPrenom(e.target.value)}
                      placeholder="Votre prénom"
                      className="mt-2 bg-[#FAF7F8] border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl h-12"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-[#5F6170] font-medium">
                      Email
                    </label>
                    <Input
                      data-testid="lead-magnet-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      className="mt-2 bg-[#FAF7F8] border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl h-12"
                      disabled={loading}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="lead-magnet-submit"
                    className="w-full bg-[#2847B8] text-white hover:bg-[#3A2E6E] disabled:opacity-60 transition-all duration-300 rounded-full h-12 font-medium inline-flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Envoi…
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" /> Télécharger le guide
                      </>
                    )}
                  </button>
                  {done && (
                    <p
                      data-testid="lead-magnet-success"
                      className="text-xs text-[#3A2E6E] text-center"
                    >
                      Merci, vérifiez votre boîte de réception.
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
